import api from '@/lib/api'
import type { TrialRiskInfo } from '@/services/agencies'

export interface AdminDashboardStats {
    totalAgencies: number
    activeSubscriptions: number
    trialing: number
    mrr: number
    totalForms: number
    totalSubmissions: number
    completedSubmissions?: number
    partialSubmissions?: number
    totalLeads: number
    completedLeads?: number
    partialLeads?: number
    appointments: number
    periodComparison?: {
        agenciesChange: string
        mrrChange: string
        submissionsChange: string
        leadsChange: string
    }
}

// One day of GET /admin/dashboard/chart. The route returns every metric for
// every day and ignores any metric parameter.
export interface AdminChartRow {
    date: string
    signups: number
    revenue: number
    submissions: number
    completedSubmissions: number
    leads: number
    appointments: number
}

export interface TopPerformingAgency {
    id: string
    name: string
    submissions: number
    completedSubmissions?: number
    partialSubmissions?: number
    leads: number
    completedLeads?: number
    partialLeads?: number
    plan: string | null
}

export interface RecentSignup {
    id: string
    name: string
    plan: string | null
    stripeStatus: string
    createdAt: string
}

export interface ActiveTrial {
    id: string
    name: string
    plan: string | null
    trialEndDate: string | null
    daysRemaining: number
    submissions: number
    leads: number
    trialRisk?: TrialRiskInfo | null
    cancelAtPeriodEnd?: boolean
}

// GET /admin/setup-wizard/summary: distinct agencies at each point of the
// setup wizard over the last `days` days. reachedStep[n] counts the agencies
// whose furthest step is at least n.
export interface SetupWizardFunnel {
    days: number
    started: number
    reachedStep: Record<1 | 2 | 3, number>
    leftEarly: number
    testLeads: number
    published: number
}

export const AdminDashboardService = {
    getStats: async (range: string = '1m') => {
        const response = await api.get('/admin/dashboard/stats', { params: { range } })
        return response.data.data as AdminDashboardStats
    },
    getChartRows: async (range: string = '1m') => {
        const response = await api.get('/admin/dashboard/chart', { params: { range } })
        return response.data.data as AdminChartRow[]
    },
    getTopAgencies: async (range: string = '1m') => {
        const response = await api.get('/admin/dashboard/top-agencies', { params: { range } })
        return response.data.data as TopPerformingAgency[]
    },
    getRecentSignups: async () => {
        const response = await api.get('/admin/dashboard/recent-signups')
        return response.data.data as RecentSignup[]
    },
    getActiveTrials: async () => {
        const response = await api.get('/admin/dashboard/active-trials')
        return response.data.data as ActiveTrial[]
    },
    getSetupWizardFunnel: async (days: number) => {
        const response = await api.get('/admin/setup-wizard/summary', { params: { days } })
        return response.data.data as SetupWizardFunnel
    }
}
